import type { WorkBuddyGroup } from './workBuddyAuthorize.types'

/**
 * WorkBuddy 开放平台登记的外部授权页路径。
 * 授权完成前，任何跳转或 URL 改写都必须保留原路径和一次性 `code`。
 * 当前守卫覆盖 App.handleFetchGroupInfo、App.handleAfterLogin、微信 onekey code 分流、
 * App.renderContent、今日打卡内测弹窗，以及登录页 BindMobile 返回时的查询参数清理。
 * MCP OAuth 登录承接使用独立的 WORKBUDDY_OAUTH_LOGIN_PATH；新增登录、全局弹层或
 * 路由改写逻辑时必须同时检查两条路径。
 */
export const WORKBUDDY_AUTHORIZE_PATH = '/workbuddy/authorize'

/** MCP OAuth 专用登录承接页；与 Buddy 一次性 code 授权页分开，避免两种 code 混淆。 */
export const WORKBUDDY_OAUTH_LOGIN_PATH = '/workbuddy/oauth/login'

const WORKBUDDY_OAUTH_BRIDGE_PATH = '/web/workgroup/workbuddy/oauth/authorize'

export const WORKBUDDY_OAUTH_REDIRECT_STORAGE_KEY =
  'workbuddy_oauth_login_redirect_attempt'

export type WorkBuddyOAuthRedirectAttempt =
  | 'allowed'
  | 'loop_detected'
  | 'storage_unavailable'

// MCP OAuth 登录完成后只能返回今日水印相机自有的 WebXCameraAPI 桥接入口。
// 不能仅判断 https，也不能接受任意 5181688 子域，否则 return_url 会成为开放重定向。
const WORKBUDDY_OAUTH_BRIDGE_ORIGINS = new Set([
  'https://webapi.5181688.com',
  'https://webapitest.xhey.top',
  'https://webapi-staging.5181688.com'
])

/**
 * 判断外部传入路径是否为 WorkBuddy 授权页。
 * 开放平台或中间跳转可能改变路径大小写或补充尾斜杠，因此不能直接全等比较。
 *
 * @param pathname - 浏览器或 React Router 提供的路径
 * @returns 规范化后是否匹配 WorkBuddy 授权页
 *
 * @example
 * ```ts
 * isWorkBuddyAuthorizePath('/WorkBuddy/Authorize/') // true
 * isWorkBuddyAuthorizePath('/workbuddy/authorize/extra') // false
 * ```
 */
export const isWorkBuddyAuthorizePath = (pathname: string): boolean =>
  pathname.toLowerCase().replace(/\/+$/, '') === WORKBUDDY_AUTHORIZE_PATH

/** 判断当前路径是否为 MCP OAuth 登录承接页，兼容外部跳转补充的尾斜杠和大小写。 */
export const isWorkBuddyOAuthLoginPath = (pathname: string): boolean =>
  pathname.toLowerCase().replace(/\/+$/, '') === WORKBUDDY_OAUTH_LOGIN_PATH

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const readString = (value: Record<string, unknown>, key: string): string => {
  const field = value[key]
  return typeof field === 'string' ? field.trim() : ''
}

/**
 * 从 WorkBuddy 回调地址中读取一次性授权码并去除首尾空白。
 *
 * @param search - 当前地址的查询字符串
 * @returns 去除首尾空白后的授权码；参数不存在时返回空字符串
 *
 * @example
 * ```ts
 * parseWorkBuddyAuthorizationCode('?code=%20buddy-code%20') // 'buddy-code'
 * ```
 */
export const parseWorkBuddyAuthorizationCode = (search: string): string =>
  new URLSearchParams(search).get('code')?.trim() || ''

/**
 * 读取并校验 MCP OAuth 登录后的返回地址。
 *
 * Buddy 账号绑定使用 `/workbuddy/authorize?code=...`，MCP OAuth 使用独立的
 * `/workbuddy/oauth/login?return_url=...`。这里仍兼容修复发布前生成的旧授权页
 * return_url 链接。返回地址携带 client_id、state、PKCE challenge 和 scope，必须
 * 原样保留；同时只允许跳回自有 WebXCameraAPI 的固定 OAuth 桥接路径，避免形成
 * 开放重定向。
 *
 * @param search - 当前地址的查询字符串
 * @returns 通过白名单校验的绝对返回地址；缺失或不可信时返回空字符串
 */
export const parseWorkBuddyOAuthReturnURL = (search: string): string => {
  const rawReturnURL = new URLSearchParams(search).get('return_url')?.trim()
  if (!rawReturnURL) return ''

  try {
    const returnURL = new URL(rawReturnURL)
    if (
      !WORKBUDDY_OAUTH_BRIDGE_ORIGINS.has(returnURL.origin) ||
      returnURL.pathname !== WORKBUDDY_OAUTH_BRIDGE_PATH ||
      returnURL.username !== '' ||
      returnURL.password !== '' ||
      returnURL.hash !== ''
    ) {
      return ''
    }
    // 校验使用 URL 的规范化字段，回跳仍使用原值，确保 state、PKCE challenge
    // 及其编码形式不被浏览器重新序列化。
    return rawReturnURL
  } catch (_error) {
    return ''
  }
}

/**
 * 登记当前标签页对同一 MCP OAuth return_url 的回跳尝试。
 *
 * WebX 与前端的登录态在极端情况下可能短暂不一致：WebX 再次送回登录页时，
 * 已恢复 Redux user 的前端会立即再次回跳。sessionStorage 跨整页导航保留，
 * 因而第二次遇到同一个 return_url 时必须停止，避免用户陷入无法后退的 replace 循环。
 * 存储不可用时选择停止而不是继续跳转，确保隐私模式或浏览器策略限制下也不会死循环。
 *
 * @param returnURL - 已通过白名单校验的 WebX OAuth 桥接地址
 * @param storage - 默认使用当前标签页 sessionStorage；参数用于测试
 * @returns 首次回跳可继续，重复回跳或存储不可用时返回对应阻断原因
 */
export const registerWorkBuddyOAuthRedirectAttempt = (
  returnURL: string,
  storage: Pick<Storage, 'getItem' | 'setItem'> = window.sessionStorage
): WorkBuddyOAuthRedirectAttempt => {
  try {
    const stored = storage.getItem(WORKBUDDY_OAUTH_REDIRECT_STORAGE_KEY)
    let previousReturnURL = ''
    let previousCount = 0
    if (stored) {
      const parsed: unknown = JSON.parse(stored)
      if (isRecord(parsed)) {
        previousReturnURL = readString(parsed, 'return_url')
        const count = parsed.count
        previousCount =
          typeof count === 'number' && Number.isInteger(count) && count > 0
            ? count
            : 0
      }
    }

    const count = previousReturnURL === returnURL ? previousCount + 1 : 1
    storage.setItem(
      WORKBUDDY_OAUTH_REDIRECT_STORAGE_KEY,
      JSON.stringify({ return_url: returnURL, count })
    )
    return count > 1 ? 'loop_detected' : 'allowed'
  } catch (_error) {
    return 'storage_unavailable'
  }
}

/**
 * 在 Redux 用户数据边界上校验并整理 WorkBuddy 可授权团队。
 *
 * @param value - 当前账号返回的原始团队数据
 * @returns 仅包含有效长 ID 和团队名称的标准化团队列表
 *
 * @example
 * ```ts
 * normalizeWorkBuddyGroups([{ group_id: 'g1', group_name: '项目一组' }])
 * // [{ id: 'g1', inviteId: '', name: '项目一组', color: undefined }]
 * ```
 */
export const normalizeWorkBuddyGroups = (value: unknown): WorkBuddyGroup[] => {
  if (!Array.isArray(value)) return []

  return value.reduce<WorkBuddyGroup[]>((groups, item) => {
    if (!isRecord(item)) return groups

    const id = readString(item, 'group_id')
    const name = readString(item, 'group_name')
    if (!id || !name) return groups

    groups.push({
      id,
      inviteId: readString(item, 'invite_id'),
      name,
      color: readString(item, 'group_color') || undefined
    })
    return groups
  }, [])
}

/**
 * 计算 WorkBuddy 授权页的初始团队，兼容今日水印相机长、短团队 ID。
 *
 * @param groups - 已标准化的可授权团队
 * @param currentGroupId - 当前已选团队的长 ID 或短 ID
 * @returns 当前团队的长 ID；没有当前团队且仅有一个团队时返回该团队 ID
 *
 * @example
 * ```ts
 * selectInitialWorkBuddyGroupId(
 *   [{ id: 'group-1', inviteId: 'short-1', name: '项目一组' }],
 *   'short-1'
 * ) // 'group-1'
 * ```
 */
export const selectInitialWorkBuddyGroupId = (
  groups: WorkBuddyGroup[],
  currentGroupId: string
): string => {
  const normalizedCurrentGroupId = currentGroupId.trim()
  const currentGroup = groups.find(
    (group) =>
      group.id === normalizedCurrentGroupId ||
      group.inviteId === normalizedCurrentGroupId
  )
  if (currentGroup) return currentGroup.id
  return groups.length === 1 ? groups[0].id : ''
}

const ERROR_MESSAGES: Record<string, string> = {
  today_login_required: '登录状态已失效，请刷新页面重新登录。',
  group_required: '请选择要授权的团队。',
  invalid_group: '团队不存在或你已不在该团队中，请重新选择。',
  authorization_code_invalid:
    '授权码无效、已过期或已使用，请返回 WorkBuddy 重新发起连接。',
  binding_conflict: '该 WorkBuddy 账号已绑定其他今日水印相机账号或团队。',
  workbuddy_client_invalid: 'WorkBuddy 应用配置异常，请联系管理员处理。',
  workbuddy_unavailable: 'WorkBuddy 服务暂时不可用，请稍后重试。',
  origin_not_allowed:
    '当前页面来源不受信任，请从今日水印相机官方页面重新发起授权。',
  invalid_request: '授权请求不完整，请返回 WorkBuddy 重新发起。',
  server_error: '授权服务暂时异常，请稍后重试。',
  network_error: '网络连接失败，请检查网络后重试。',
  request_timeout: '请求超时，请稍后重试。',
  invalid_response: '服务返回异常，请稍后重试。'
}

/**
 * 将 WorkBuddy 绑定接口的稳定错误码转换为用户可理解的中文提示。
 *
 * @param code - WebXCameraAPI 或 adapter 返回的稳定错误码
 * @returns 对应提示；未知错误码返回通用授权失败提示
 *
 * @example
 * ```ts
 * getWorkBuddyAuthorizationErrorMessage('binding_conflict')
 * // '该 WorkBuddy 账号已绑定其他今日水印相机账号或团队。'
 * ```
 */
export const getWorkBuddyAuthorizationErrorMessage = (code: string): string =>
  ERROR_MESSAGES[code] || '授权失败，请稍后重试。'
